import { useLogImpression, useTracker } from '@archipro-website/tracker';
import { Image } from '@archipro/rocco/components/image';
import type { TagType } from '@modules/product-tagging/type';
import { Link } from '@remix-run/react';
import { useRef } from 'react';
import { isUserLoggedIn, useUser } from '~/modules/user/hook/use-user';

interface TagHoverContentProps {
    bottomActions?: React.ReactNode[];
    showImageOverlay?: boolean;
    marker: TagType;
}

export const TagHoverContent = ({
    marker,
    bottomActions,
    showImageOverlay = false,
}: TagHoverContentProps) => {
    const { product } = marker;

    const tracker = useTracker();
    const user = useUser();
    const isLoggedIn = isUserLoggedIn(user);

    const ref = useRef<HTMLDivElement | null>(null);
    useLogImpression({
        data: {
            professionalID: product?.professionalID ?? 0,
            itemID: product?.id ?? 0,
            type: isLoggedIn ? 'ProductTagLoggedIn' : 'ProductTagLoggedOut',
        },
        ref,
    });

    if (!product) {
        return null;
    }

    const onClick = () => {
        tracker.log('TagHoverClick', {
            url: new URL(window.location.href),
            data: {
                ExtraData: product.id,
                professionalID: product.professionalID,
                tagID: product.tagId,
            },
        });
    };

    const { imageUrl, title, professional } = product;

    return (
        <div
            ref={ref}
            className="relative bg-gray-50/70 mx-2 rounded-lg overflow-hidden p-3 flex flex-col items-center gap-3.5 shadow-[inset_0px_0px_4px_0px_rgba(0,0,0,0.25)] backdrop-blur-[5px]"
        >
            {showImageOverlay && (
                <div className="absolute top-0 left-0 w-full bg-black/50 flex flex-col px-3 py-2 z-fixed gap-1">
                    <span className="text-white text-xs font-medium">
                        {marker.member
                            ? `${marker.member.firstName} ${marker.member.lastName}`
                            : `Deleted user`}
                    </span>
                    <span className="text-white text-xs">
                        {marker.member ? `${marker.member.email}` : ''}
                    </span>
                </div>
            )}
            <div className="bg-gray-50 rounded-lg flex items-center justify-center shrink-0 overflow-hidden w-46.5 relative">
                <Link to={product.link} onClick={onClick}>
                    <Image
                        src={imageUrl ?? ''}
                        alt={title}
                        width={186}
                        height={186}
                    />
                </Link>

                {showImageOverlay && bottomActions && (
                    <div className="absolute bottom-2 right-2 w-full flex items-center gap-2 justify-end">
                        {bottomActions.map((action) => action)}
                    </div>
                )}
            </div>
            <div className="flex flex-col gap-0.5 w-46.5">
                <Link
                    to={product.link}
                    className="text-3.5 font-medium truncate w-full"
                    onClick={onClick}
                >
                    <span className="hover:underline">{title}</span>
                </Link>
                {product.link && (
                    <Link
                        className="text-3 text-gray-500 truncate w-full"
                        to={product.link}
                    >
                        <span className="hover:underline">{professional}</span>
                    </Link>
                )}
            </div>
        </div>
    );
};
